import {
    IReactionDisposer,
    onBecomeObserved,
    onBecomeUnobserved,
    reaction,
} from 'mobx';
import {
    fromPromise,
    IBasePromiseBasedObservable,
    IPromiseBasedObservable,
    PENDING,
    PromiseState,
    REJECTED,
} from 'mobx-utils';
import { computed, makeObservable } from 'mobx';
import { PromiseLikeUnwrap } from './utilityTypes';

export type ResolveFn<T> = (value: T | PromiseLike<T>) => void;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RejectFn = (reason: any) => void;
export type Executor<T> = (
    resolveFn: ResolveFn<T>,
    rejectFn: RejectFn
) => (() => IReactionDisposer) | void;
export type RequiredOnFulfilled<T, R> = (value: T) => R | PromiseLike<R>;
export type OnFulFilled<T, R> = RequiredOnFulfilled<T, R> | undefined | null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RequiredOnReject<R> = (reason: any) => R | PromiseLike<R>;
export type OnRejected<R> = RequiredOnReject<R> | undefined | null;

export type CaseHandlers<U, T> = {
    pending?: (t?: unknown) => U;
    fulfilled?: (t: T) => U;
    rejected?: (e: unknown) => U;
};

export interface ILazyObservablePromise<T>
    extends IBasePromiseBasedObservable<T> {
    later<R1 = T, R2 = never>(
        onFulfilled: OnFulFilled<T, R1>,
        onRejected: OnRejected<R2>
    ): ILazyObservablePromise<R1 | R2>;
}

export class LazyObservablePromise<T> implements ILazyObservablePromise<T> {
    private executor: Executor<T>;
    private observablePromise?: IPromiseBasedObservable<T>;
    private disposer?: IReactionDisposer;
    private watcher?: () => IReactionDisposer;
    private observersCount = 0;
    private behaveLikeThen = false;
    isPromiseBasedObservable: true;

    get value(): T | undefined {
        return this.observablePromise
            ? (this.observablePromise.value as T)
            : undefined;
    }

    get state(): PromiseState {
        return this.observablePromise?.state ?? PENDING;
    }

    case<U>(handlers: CaseHandlers<U, T>, defaultFulfilled?: boolean): U {
        if (this.observablePromise)
            return this.observablePromise.case(handlers, defaultFulfilled);

        if (this.state === PENDING && handlers.pending)
            return handlers.pending(undefined);

        return undefined as unknown as U;
    }

    constructor(executor: Executor<T>, syncExecutor = false) {
        this.executor = executor;
        this.isPromiseBasedObservable = true;

        makeObservable(this, {
            value: computed,
            state: computed,
        });

        this.init();

        if (syncExecutor) {
            this.createPromise();
        }
    }

    private init() {
        onBecomeObserved(this, 'value', () => this.onObserve());
        onBecomeObserved(this, 'state', () => this.onObserve());
        onBecomeUnobserved(this, 'value', () => this.onUnobserve());
        onBecomeUnobserved(this, 'state', () => this.onUnobserve());
    }

    private autoDispose() {
        if (this.observersCount > 0) return;
        if (this.disposer && !this.behaveLikeThen) {
            this.disposer();
            this.disposer = undefined;
        }
    }

    private startWatcher() {
        if (this.watcher && this.disposer === undefined)
            this.disposer = this.watcher();
    }

    private onUnobserve() {
        this.observersCount--;
        this.autoDispose();
    }

    private onObserve() {
        this.observersCount++;
        this.dispatchEvaluation(false);
    }

    private createPromise() {
        if (!this.observablePromise) {
            this.observablePromise = fromPromise(
                new Promise((resolve, reject) => {
                    const watcher = this.executor(resolve, reject);
                    if (!watcher) return;
                    this.watcher = watcher;
                })
            );
        }
    }

    private dispatchEvaluation(fromThen: boolean) {
        this.createPromise();
        if (fromThen) this.behaveLikeThen = true;
        this.startWatcher();
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this.observablePromise!;
    }

    static resolve<T>(
        value: T | LazyObservablePromise<T>
    ): LazyObservablePromise<T>;
    static resolve(): LazyObservablePromise<void>;
    static resolve<T>(value?: T): LazyObservablePromise<T | void> {
        let result: LazyObservablePromise<void | typeof value>;

        if (value === undefined) {
            result = new LazyObservablePromise<void>(resolve =>
                resolve(undefined)
            );
            result.observablePromise = fromPromise.resolve(undefined);
        } else if (value instanceof LazyObservablePromise) {
            if (value.state === PENDING) return value.later();
            else return this.resolve(value.value as T);
        } else {
            result = new LazyObservablePromise<typeof value>(resolve =>
                resolve(value)
            );
            result.observablePromise = fromPromise.resolve(value);
        }

        return result;
    }

    static reject(reason?: unknown): LazyObservablePromise<never> {
        const result = new LazyObservablePromise<never>((_, reject) =>
            reject(reason)
        );
        result.observablePromise = fromPromise.reject(reason);
        return result;
    }

    static all<A extends readonly LazyObservablePromise<unknown>[]>(
        promises: A,
        syncExecutor = false
    ): LazyObservablePromise<{
        [K in keyof A]: PromiseLikeUnwrap<A[K]>;
    }> {
        return new LazyObservablePromise(
            (resolveFn, rejectFn) => () =>
                reaction(
                    () =>
                        promises.map(promise => [promise.state, promise.value]),
                    (_0, _1, reaction) => {
                        const rejected = promises.filter(
                            promise => promise.state === 'rejected'
                        );
                        if (rejected.length > 0) {
                            rejectFn(rejected[0].value);
                            reaction.dispose();
                        }

                        const pending = promises.filter(
                            promises => promises.state === 'pending'
                        );
                        if (pending.length > 0) return;

                        const fulfilled = promises.map(
                            promise => promise.value
                        );

                        resolveFn(
                            fulfilled as unknown as {
                                [K in keyof A]: PromiseLikeUnwrap<A[K]>;
                            }
                        );
                        reaction.dispose();
                    },
                    { fireImmediately: true }
                ),
            syncExecutor
        );
    }

    then<R1 = T, R2 = never>(
        onFulfilled?: OnFulFilled<T, R1>,
        onRejected?: OnRejected<R2>
    ): PromiseLike<R1 | R2> {
        return this.dispatchEvaluation(true).then(onFulfilled, onRejected);
    }

    sideEffect<F, R2 = never>(
        forwardFn: RequiredOnFulfilled<T, F>,
        onRejected?: OnRejected<R2>,
        syncExecutor = false
    ): LazyObservablePromise<T | R2> {
        return this.later(
            result => {
                const sideEffect = forwardFn(result);
                const newPromise = toLazyObservablePromise(
                    sideEffect instanceof Object && 'then' in sideEffect
                        ? sideEffect
                        : LazyObservablePromise.resolve(sideEffect as F)
                );
                return newPromise.later(() => result);
            },
            onRejected,
            syncExecutor
        );
    }

    later<R1 = T, R2 = never>(
        onFulfilled?: OnFulFilled<T, R1>,
        onRejected?: OnRejected<R2>,
        syncExecutor = false
    ): LazyObservablePromise<R1 | R2> {
        return new LazyObservablePromise((resolveFn, rejectFn) => {
            const reject = (value: unknown) => {
                if (onRejected) {
                    try {
                        resolveFn(onRejected(value));
                    } catch (exception) {
                        rejectFn(exception);
                    }
                } else {
                    rejectFn(value);
                }
            };

            const resolve = (value: unknown) => {
                if (onFulfilled) {
                    try {
                        resolveFn(onFulfilled(value as T));
                    } catch (exception) {
                        rejectFn(exception);
                    }
                } else {
                    resolveFn(value as R1);
                }
            };

            return () => {
                return reaction(
                    () => [this.state, this.value] as const,
                    ([state, value], _, reaction) => {
                        if (state === PENDING) return;
                        if (state === REJECTED) reject(value);
                        else resolve(value);
                        reaction.dispose();
                    },
                    { fireImmediately: true }
                );
            };
        }, syncExecutor);
    }
}

export function toLazyObservablePromise<T>(
    promise: PromiseLike<T>,
    syncExecutor = false
): LazyObservablePromise<T> {
    if (promise instanceof LazyObservablePromise) return promise;
    return new LazyObservablePromise((resolve, reject) => {
        promise.then(resolve, reject);
    }, syncExecutor);
}
